import { Controller, useForm } from 'react-hook-form';
import { ApolloCache } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Coupon,
  Coupon_GetAllCouponsDocument,
  Coupon_GetAllCouponsQuery,
  CouponType,
  useCoupon_CreateCouponMutation,
  useCoupon_UpdateCouponMutation,
} from 'queries';
import * as yup from 'yup';

import FormDateField from 'common/form-date-field';
import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  coupon?: Coupon;
  onExitClick: () => void;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
}));

type CouponFormValues = {
  description: string;
  amount: number;
  code: string;
  couponType: CouponType;
  expirationDate: Date | null;
  allowedUses?: string;
  isActive: boolean;
};

const requiredTextFields = ['description', 'amount', 'code'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});

const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

export default function CouponForm({ coupon, onExitClick }: Props) {
  const classes = useStyles();

  // coupon code and description fields are only editable when creating
  const isNewCoupon = !coupon;

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
  } = useForm<CouponFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    values: {
      amount: coupon?.amount || 9900,
      isActive: coupon?.isActive || true,
      code: coupon?.code || '',
      couponType: coupon?.couponType || CouponType.FlatFee,
      description: coupon?.description || '',
      allowedUses: coupon?.allowedUses ? String(coupon.allowedUses) : '',
      expirationDate: coupon?.expirationDate ? new Date(coupon.expirationDate) : null,
    },
  });

  const readCouponQuery = (
    store: ApolloCache<Coupon_GetAllCouponsQuery>,
  ): Coupon_GetAllCouponsQuery | null => {
    return store.readQuery<Coupon_GetAllCouponsQuery>({
      query: Coupon_GetAllCouponsDocument,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writeCouponQuery = (store: ApolloCache<Coupon_GetAllCouponsQuery>, coupons: any) => {
    store.writeQuery<Coupon_GetAllCouponsQuery>({
      query: Coupon_GetAllCouponsDocument,
      data: {
        data: coupons,
      },
    });
  };

  const [create, { loading: createExecuting }] = useCoupon_CreateCouponMutation({
    update: (store, { data }) => {
      const couponData = readCouponQuery(store);
      if (couponData) {
        const coupons = [...couponData.data, data!.data];
        writeCouponQuery(store, coupons);
      }
      onExitClick();
    },
    onError: (e) =>
      showAlert({
        message: `Error creating coupon: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const [update, { loading: updateExecuting }] = useCoupon_UpdateCouponMutation({
    update: (store) => {
      const couponData = readCouponQuery(store);
      if (couponData) {
        writeCouponQuery(store, couponData.data);
      }
      onExitClick();
    },
    onError: (e) =>
      showAlert({
        message: `Error updating coupon: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const submitForm = async ({ allowedUses, code, ...formData }: CouponFormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      ...formData,
    };
    const amount = parseInt(input.amount);

    if (allowedUses) {
      input.allowedUses = parseInt(allowedUses);
    }

    if (coupon?.id) {
      update({
        variables: {
          input: {
            ...input,
            amount,
            id: coupon.id,
          },
        },
      });
    } else {
      // creating a new coupon
      create({
        variables: {
          input: {
            ...input,
            amount,
            code,
          },
        },
      });
    }
  };

  const couponType = watch('couponType');

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Coupon Details
      </Typography>
      <form id="coupon-form" className={classes.form} onSubmit={handleSubmit(submitForm)}>
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={4}>
              <Controller
                name="code"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    autoFocus
                    inputProps={{ maxLength: 80 }}
                    disabled={!isNewCoupon}
                    label="Code"
                    errorMessage={errors.code?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller
                name="description"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    name="description"
                    inputProps={{ maxLength: 80 }}
                    disabled={!isNewCoupon}
                    label="Description"
                    errorMessage={errors.description?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={4}>
              <Controller
                name="couponType"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      { label: 'Flat fee', value: CouponType.FlatFee },
                      { label: 'Percentage off', value: CouponType.PercentageOff },
                    ]}
                    label="Type"
                    errorMessage={errors.couponType?.message}
                  />
                )}
              />
              {errors.couponType?.message && (
                <FormInputErrorMessage error={errors.couponType?.message} />
              )}
            </Grid>
            <Grid className={classes.inputGrid} item xs={12} sm={4}>
              <Controller
                name="amount"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    type="number"
                    inputProps={{ maxLength: 80 }}
                    label={`Amount (${couponType === 'FLAT_FEE' ? 'in cents' : 'percentage'})`}
                    errorMessage={errors.amount?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={4}>
              <Controller
                name="allowedUses"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    label="Allowed Uses (per portal)"
                    errorMessage={errors.allowedUses?.message}
                  />
                )}
              />
            </Grid>
            <Grid className={classes.inputGrid} item xs={12} sm={7}>
              <Controller
                name="expirationDate"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <FormDateField
                    {...{ value, onChange, name }}
                    label="Expiration Date"
                    errorMessage={errors.expirationDate?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="coupon-form"
                color="secondary"
                variant="contained"
                disabled={isSubmitting || createExecuting || updateExecuting}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
